import React, { useCallback, useState } from 'react'
import { graphql, useFragment } from 'react-relay'
import { Trans, useLingui } from '@lingui/react/macro'
import { YStack, XStack, Separator, isWeb } from 'tamagui'
import { useFormikContext } from 'formik'
import * as Yup from 'yup'

import { useMutation } from '@data/useMutation'
import { Dialog, Form, Icon, Paragraph } from '@newComponents'
import { Button } from '@newComponents/Button/Button'
import { EmojiIcon } from '@newComponents/EmojiIcon/EmojiIcon'

import { EventScreenInviteDialog_event$key } from './__generated__/EventScreenInviteDialog_event.graphql'
import { EventScreenInviteDialogMutation } from './__generated__/EventScreenInviteDialogMutation.graphql'

type InviteFormValues = {
  rows: { firstName: string; lastName: string; email: string }[]
}

const createValidationSchema = (
  t: (strings: TemplateStringsArray, ...values: any[]) => string
) => {
  const rowSchema = Yup.object().shape({
    email: Yup.string()
      .email(t`Please enter a valid email`)
      .required(t`Email is required`),
    firstName: Yup.string(),
    lastName: Yup.string(),
  })

  return Yup.object().shape({
    rows: Yup.array().of(rowSchema),
  })
}

export const EventScreenInviteDialog = ({
  event,
}: {
  event: EventScreenInviteDialog_event$key
}) => {
  const data = useFragment(
    graphql`
      fragment EventScreenInviteDialog_event on Event {
        id
        name
      }
    `,
    event
  )

  const { t } = useLingui()
  const [open, setOpen] = useState(false)
  const [sentEmails, setSentEmails] = useState<string[]>([])

  const inviteMutation = useMutation<EventScreenInviteDialogMutation>(graphql`
    mutation EventScreenInviteDialogMutation($input: InviteToEventInput!) {
      inviteToEvent(input: $input) {
        success
      }
    }
  `)

  const handleSubmit = useCallback(
    async (
      values: InviteFormValues,
      formikHelpers: {
        resetForm: () => void
        setStatus: (status: string | undefined) => void
      }
    ) => {
      const sent: string[] = []

      for (const row of values.rows) {
        if (!row.email.trim()) continue

        try {
          await inviteMutation({
            variables: {
              input: {
                eventId: data.id,
                email: row.email.trim(),
                firstName: row.firstName.trim() || undefined,
                lastName: row.lastName.trim() || undefined,
              },
            },
          })
          sent.push(row.email.trim())
        } catch {
          const email = row.email.trim()
          formikHelpers.setStatus(
            t`Something went wrong sending the invite to ${email}. Please try again.`
          )
          // Still show any that succeeded before the failure
          if (sent.length) {
            setSentEmails((prev) => [...prev, ...sent])
          }
          return
        }
      }

      setSentEmails((prev) => [...prev, ...sent])
      formikHelpers.resetForm()
    },
    [inviteMutation, data.id]
  )

  const handleOpenChange = useCallback((isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) {
      setSentEmails([])
    }
  }, [])

  const initialValues: InviteFormValues = {
    rows: [{ firstName: '', lastName: '', email: '' }],
  }

  return (
    <Dialog modal open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger tag='dialog'>
        <XStack
          gap='$2'
          alignItems='center'
          justifyContent='center'
          flexShrink={0}
          cursor='pointer'
          height={32}
          paddingHorizontal='$3'
          borderRadius={16}
          hoverStyle={{ backgroundColor: '$backgroundHover' }}
          pressStyle={{ backgroundColor: '$backgroundPress' }}
        >
          <Icon name='mail' size='$5' color='$colorMuted' />
          <Paragraph size='$4' color='$colorMuted' fontWeight='$5'>
            <Trans>Invite</Trans>
          </Paragraph>
        </XStack>
      </Dialog.Trigger>

      <Dialog.Adapt when='xs'>
        <Dialog.Sheet key='sheet'>
          <Dialog.Sheet.Handle zIndex={200_000} />
          <Dialog.Sheet.Frame
            padding={0}
            gap='$4'
            shadowColor='$shadowColor'
            shadowRadius='$2'
            borderTopRightRadius='$6'
            borderTopLeftRadius='$6'
          >
            <Dialog.Sheet.ScrollView paddingTop='$6' paddingHorizontal='$6'>
              <YStack gap='$4'>
                {isWeb ? (
                  <InviteDialogContent
                    eventName={data.name}
                    initialValues={initialValues}
                    sentEmails={sentEmails}
                    onSubmit={handleSubmit}
                  />
                ) : (
                  <Dialog.Adapt.Contents />
                )}
              </YStack>
            </Dialog.Sheet.ScrollView>
          </Dialog.Sheet.Frame>
        </Dialog.Sheet>
      </Dialog.Adapt>

      <Dialog.Portal>
        <Dialog.Content
          elevate
          key='content'
          gap='$4'
          padding='$2'
          minWidth={400}
          maxWidth={480}
        >
          <InviteDialogContent
            eventName={data.name}
            initialValues={initialValues}
            sentEmails={sentEmails}
            onSubmit={handleSubmit}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  )
}

const InviteDialogContent = ({
  eventName,
  initialValues,
  sentEmails,
  onSubmit,
}: {
  eventName: string
  initialValues: InviteFormValues
  sentEmails: string[]
  onSubmit: (
    values: InviteFormValues,
    helpers: {
      resetForm: () => void
      setStatus: (status: string | undefined) => void
    }
  ) => Promise<void>
}) => {
  const { t } = useLingui()
  const validationSchema = createValidationSchema(t)

  return (
    <YStack gap='$4' padding='$4'>
      <XStack gap='$4' alignItems='center'>
        <EmojiIcon size='$8' name='sparkling-heart' />

        <YStack gap='$1' flex={1}>
          <Dialog.Title>
            <Trans>Invite your Friends</Trans>
          </Dialog.Title>

          <Dialog.Description color='$colorMuted'>
            <Trans>
              The more the merrier! Send a personalized invite to join you at{' '}
              {eventName}.
            </Trans>
          </Dialog.Description>
        </YStack>
      </XStack>

      {!!sentEmails.length && (
        <YStack gap='$2'>
          {sentEmails.map((email) => (
            <XStack key={email} gap='$2' alignItems='center'>
              <Icon name='checkmark-filled' size='$5' color='$green10' />
              <Paragraph size='$4' color='$green10'>
                <Trans>Sent to {email}</Trans>
              </Paragraph>
            </XStack>
          ))}
        </YStack>
      )}

      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        <InviteFormFields />
      </Form>
    </YStack>
  )
}

const InviteFormFields = () => {
  const { t } = useLingui()
  const { values, setFieldValue } = useFormikContext<InviteFormValues>()

  const handleAddRow = useCallback(() => {
    void setFieldValue('rows', [
      ...values.rows,
      { firstName: '', lastName: '', email: '' },
    ])
  }, [values.rows, setFieldValue])

  return (
    <YStack gap='$4'>
      {values.rows.map((_, index) => (
        <React.Fragment key={index}>
          {index > 0 && <Separator borderColor='$borderColor' />}
          <YStack gap='$3'>
            <XStack gap='$4'>
              <Form.Field
                name={`rows.${index}.firstName`}
                label={t`First Name`}
                showError={false}
              >
                <Form.TextInput
                  placeholder={t`First Name`}
                  autoCapitalize='words'
                />
              </Form.Field>

              <Form.Field
                name={`rows.${index}.lastName`}
                label={t`Last Name`}
                showError={false}
              >
                <Form.TextInput
                  placeholder={t`Last Name`}
                  autoCapitalize='words'
                />
              </Form.Field>
            </XStack>

            <Form.Field name={`rows.${index}.email`} label={t`Email`}>
              <Form.TextInput
                placeholder={t`email@example.com`}
                autoCapitalize='none'
                keyboardType='email-address'
                inputMode='email'
              />
            </Form.Field>
          </YStack>
        </React.Fragment>
      ))}

      <Form.Status />

      <XStack justifyContent='flex-end' gap='$3'>
        <Button secondary height={38} onPress={handleAddRow}>
          <Button.Text>
            <Trans>Invite Another</Trans>
          </Button.Text>
        </Button>

        <Form.SubmitButton alignItems='flex-end'>
          <Trans>Send</Trans>
        </Form.SubmitButton>
      </XStack>
    </YStack>
  )
}

export default EventScreenInviteDialog
